import { useState } from 'react'
import { AxiosError } from 'axios'
import { Flex, Spinner, Text } from '@radix-ui/themes'
import { useParams } from '@tanstack/react-router'
import { IconPlus } from '@tabler/icons-react'
import { useDeleteUser } from '@/services/userService'
import { ListOfClass } from '@/types/student.type'
import { toast } from 'sonner'
import studentDefaultAvatar from '@/assets/svg/student_avatar_default.svg'
import { cn } from '@/lib/utils'
import Badge from '@/components/ui/badge2'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import AddClassModal from './Modals/AddClassModal'
import ConfirmDeleteModal from './Modals/ConfirmDeleteModal'

export interface SidebarItem {
  tab: string
  name: string
  email: string
  avatar?: string
  classes: ListOfClass[]
  setTab: (id: string) => void
  refreshData: () => void
  isActive: boolean
  accountType: string
}

export default function Sidebar({
  tab,
  name,
  email,
  classes,
  avatar,
  isActive,
  accountType,
  setTab,
  refreshData,
}: SidebarItem) {
  const { studentId } = useParams({ strict: false })
  const { deleteUserAsync, isDeleting } = useDeleteUser()

  const [isOpenConfirmDelete, setIsOpenConfirmDelete] = useState(false)
  const [isOpenAddClass, setIsOpenAddClass] = useState(false)

  const handleDeleteUser = () => {
    deleteUserAsync(studentId ?? '')
      .then(() => {
        setIsOpenConfirmDelete(false)
        toast.success('User deleted!')
        refreshData()
      })
      .catch((error: AxiosError) => {
        console.log('qualify err', error)
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to upload file'
        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }
  return (
    <>
      <Flex direction='column' gap='4' p='4' className='min-w-[400px]'>
        <Flex direction='column' gap='1' className='z-10'>
          <div className='flex size-[100px] items-center justify-center rounded-full border-[4px] border-[#fff]'>
            <img
              src={avatar || studentDefaultAvatar}
              alt='avatar'
              className='z-100 h-[100px] w-[100px] rounded-full bg-white'
            />
          </div>
          <Text className='text-4xl font-[400] text-[#002652]'>{name}</Text>
          <Text className='text-[16px] font-[300] text-[#475467]'>{email}</Text>
        </Flex>
        <Flex align={'center'} gap={'3'}>
          <Badge
            style='!text-[#479DFF] !bg-white !rounded-lg'
            dotColor='#479DFF'
            title={
              accountType === 'MOODLE'
                ? 'Synced from KCB LMS'
                : 'Registered by User'
            }
          />
          {!isActive && (
            <Badge
              style='!text-[#F04438] !bg-white !rounded-lg'
              dotColor='#F04438'
              title={'Deleted User'}
            />
          )}
        </Flex>
        <Card className='border-none py-4 shadow-none'>
          <Flex
            direction='column'
            gap='4'
            className='max-h-[500px] overflow-auto'
          >
            <Text className='px-4 text-[16px] font-[600] text-[#002652]'>
              Class
            </Text>
            {classes?.length === 0 && (
              <p className='textxs px-6 text-center text-[#667085]'>
                No classes available
              </p>
            )}
            {classes.map((item: ListOfClass) => {
              // const classImg = modulesImgs.find(
              //   (module) => module.name === item.module_name
              // )?.imgUrl

              return (
                <Flex
                  direction='row'
                  gap='3'
                  className={cn(
                    'relative cursor-pointer items-start px-4 py-4 hover:bg-gray-100',
                    tab === item.class_id.toString() && 'bg-gray-200'
                  )}
                  onClick={() => setTab(item.class_id.toString())}
                >
                  <div className='absolute top-[0px] left-[50%] w-[calc(100%-32px)] -translate-x-[50%] border-t-[1px] border-[#EAECF0]'></div>

                  {/* <img
                    src={classImg ?? studentDefaultAvatar}
                    alt='module'
                    className='h-[40px] w-[40px] rounded-sm object-cover'
                  /> */}
                  <Flex direction='column' gap='1'>
                    <Text className='text-[16px] font-[400] text-[#002652]'>
                      {item.class_name}
                    </Text>
                    {/* <ModuleText text={item.module_name} /> */}
                  </Flex>
                </Flex>
              )
            })}
          </Flex>
        </Card>

        {accountType !== 'MOODLE' && (
          <Button
            disabled={!isActive}
            onClick={() => setIsOpenAddClass(true)}
            className='min-h-9 w-fit min-w-[127px] bg-[#0057B8] px-3.5'
          >
            <IconPlus />
            Add this user to classes
          </Button>
        )}

        <Button
          disabled={isDeleting || !isActive}
          onClick={() => setIsOpenConfirmDelete(true)}
          variant={'destructive'}
          className='min-h-9 w-fit min-w-[127px] bg-[#912018] px-3.5'
        >
          {isDeleting ? <Spinner /> : ' Delete this user'}
        </Button>
        {/* <Flex direction='column' gap='2'>
          <Text className='text-[16px] font-[600] text-[#002652]'>Tutor</Text>
          {tutors.map((tutor) => (
            <Flex direction='row' gap='2' className='items-center'>
              <img
                src={tutor.avatar || tutorDefaultAvatar}
                alt='tutor'
                className='h-[32px] w-[32px] rounded-full'
              />
              <Text className='text-[16px] font-[400] text-[#002652]'>
                {tutor.fullname}
              </Text>
            </Flex>
          ))}
        </Flex> */}
      </Flex>

      <ConfirmDeleteModal
        isOpenConfirmDelete={isOpenConfirmDelete}
        setIsOpenConfirmDelete={setIsOpenConfirmDelete}
        isDeleting={isDeleting}
        handleDeleteUser={() => handleDeleteUser()}
      />

      <AddClassModal
        userName={name}
        classes={classes}
        isOpen={isOpenAddClass}
        setIsOpen={setIsOpenAddClass}
        handleRefresh={() => refreshData()}
      />
    </>
  )
}
